/**
 * Route label for a load. Imported loads often keep their cities only on
 * `stops[]` while the legacy `pickup`/`delivery` objects hold empty strings,
 * which is how list views ended up rendering a bare ", → ,".
 */
const place = (leg) => {
  if (!leg) return '';
  const city = (leg.city || '').trim();
  const state = (leg.state || '').trim();
  if (city && state) return `${city}, ${state}`;
  return city || state || (leg.zip_code || '').trim() || '';
};

const legFromStops = (load, kind) => {
  const stops = (load?.stops || []).filter((s) => {
    const type = String(s?.type || s?.stop_type || '').toLowerCase();
    return kind === 'pickup'
      ? type.includes('pick') || type === 'shipper'
      : type.includes('deliv') || ['consignee', 'receiver'].includes(type);
  });
  if (!stops.length) return null;
  return kind === 'pickup' ? stops[0] : stops[stops.length - 1];
};

/** First pickup / last delivery stop — the authoritative route record. */
export const routeStop = legFromStops;

const nonEmpty = (obj) => Object.fromEntries(
  Object.entries(obj || {}).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== ''),
);

/**
 * The stop record merged with whatever the legacy pickup/delivery object
 * still holds. Older loads split the truth between the two — cities on the
 * stop, appointment date on the leg — so every view has to read both or it
 * ends up showing a dash next to perfectly good data.
 */
export function mergedLeg(load, kind) {
  const stop = routeStop(load, kind);
  const leg = load?.[kind];
  if (!stop && !leg) return null;
  return { ...(stop || {}), ...nonEmpty(leg) };
}

export const routeEnd = (load, kind) => place(mergedLeg(load, kind));

/** `Dallas, TX → Memphis, TN`, or a single known end, or '' when truly blank. */
export function formatRoute(load) {
  const from = routeEnd(load, 'pickup');
  const to = routeEnd(load, 'delivery');
  if (from && to) return `${from} → ${to}`;
  if (from) return `${from} → ?`;
  if (to) return `? → ${to}`;
  return '';
}
